import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Categories = () => {
  const { t } = useLanguage();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/categories`).then(res => setCategories(res.data)).catch(() => {});
  }, []);

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1, y: 0,
      transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  return (
    <section id="categories" data-testid="categories-section" className="py-24 md:py-32 bg-[#F4F7FB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
          className="text-[#C5A55A] text-sm font-mono tracking-[0.3em] uppercase mb-4"
        >
          {t('categories.overline')}
        </motion.p>
        <motion.h2
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
          className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#0A1628] mb-4"
          data-testid="categories-title"
        >
          {t('categories.title')}
        </motion.h2>
        <motion.p
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
          className="text-slate-600 text-base md:text-lg leading-relaxed mb-6 max-w-2xl"
        >
          {t('categories.subtitle')}
        </motion.p>

        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2.5}
          className="mb-16"
        >
          <a
            href="https://medecinesupply.com/wp-content/uploads/2023/07/product_catalogue3.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#C5A55A] hover:text-[#A68842] text-sm font-semibold transition-colors underline underline-offset-4 tracking-wider"
            data-testid="categories-catalog-link"
          >
            <ArrowUpRight className="w-4 h-4" />
            {t('categories.catalogLink')}
          </a>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat, i) => (
            <motion.div
              key={cat.id}
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i + 3}
              className={`group relative overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:border-[#C5A55A] transition-all duration-500 ${
                i === 0 ? 'md:col-span-2 lg:col-span-2 lg:row-span-2' : ''
              }`}
              data-testid={`category-card-${cat.slug}`}
            >
              <div className={`relative overflow-hidden ${i === 0 ? 'h-80 lg:h-full' : 'h-56'}`}>
                <img
                  src={cat.image_url}
                  alt={cat.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/80 via-[#0A1628]/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-2">{cat.name}</h3>
                      <p className="text-slate-300 text-sm leading-relaxed max-w-md">{cat.description}</p>
                      <p className="text-[#C5A55A] text-sm font-mono mt-3 tracking-wider">{cat.product_count} Products</p>
                    </div>
                    <div className="bg-[#C5A55A] p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ArrowUpRight className="w-5 h-5 text-[#0A1628]" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Categories;
